import { useState, useEffect } from 'react';
import { X, PenLine, AlertTriangle, History } from 'lucide-react';
import toast from 'react-hot-toast';
import { documentsApi } from '../utils/api';
import useEscClose from '../hooks/useEscClose';

const MAX_DETALLE = 200;   // lineas_detalle.detalle es VARCHAR(200)
const MIN_MOTIVO = 10;

/**
 * Corrección de la descripción de una línea de un documento ya emitido.
 *
 * Sólo cambia lo que imprime el PDF. El XML firmado que tiene Hacienda queda
 * como está — de ahí el aviso, que es sólo de pantalla: no sale en el PDF,
 * ni en el correo, ni en la FE.
 */
export default function CorregirDescripcionModal({ documento, lineas, onClose, onSaved }) {
  useEscClose(onClose);

  const [lineaId, setLineaId] = useState(lineas?.[0]?.id ?? null);
  const [descripcion, setDescripcion] = useState('');
  const [motivo, setMotivo] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [historial, setHistorial] = useState([]);
  const [verHistorial, setVerHistorial] = useState(false);

  const linea = lineas?.find(l => l.id === lineaId) || null;
  const original = linea?.detalle || '';

  // Al cambiar de línea, arrancar del texto actual de esa línea.
  useEffect(() => { setDescripcion(original); }, [lineaId, original]);

  useEffect(() => {
    documentsApi.listarCorrecciones(documento.id)
      .then(r => setHistorial(r.data?.data || []))
      .catch(() => setHistorial([]));
  }, [documento.id]);

  const limpia = descripcion.trim();
  const sinCambio = limpia === original.trim();
  const largo = limpia.length;
  const motivoOk = motivo.trim().length >= MIN_MOTIVO;
  const puedeGuardar = !!linea && !!limpia && !sinCambio && largo <= MAX_DETALLE && motivoOk && !guardando;

  const guardar = async () => {
    if (!puedeGuardar) return;
    setGuardando(true);
    try {
      await documentsApi.corregirDescripcion(documento.id, lineaId, {
        descripcion: limpia,
        motivo: motivo.trim()
      });
      toast.success('Descripción corregida');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.error || 'No se pudo corregir la descripción');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={() => !guardando && onClose()}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[92vh]" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-amber-500 to-amber-600 rounded-t-xl shrink-0">
          <div>
            <h3 className="font-semibold text-white flex items-center gap-2 text-lg">
              <PenLine className="h-5 w-5" /> Corregir descripción
            </h3>
            <p className="text-white/85 text-xs mt-0.5">{documento.consecutivo || documento.numeroConsecutivo || documento.clave}</p>
          </div>
          <button onClick={() => !guardando && onClose()} className="text-white/70 hover:text-white"><X className="h-5 w-5" /></button>
        </div>

        <div className="px-6 py-4 overflow-y-auto space-y-4">
          {/* Aviso: sólo de pantalla, sólo en este momento */}
          <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 flex gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-900 space-y-1">
              <p className="font-semibold">Esto cambia solamente el PDF.</p>
              <p>
                El XML firmado que Hacienda tiene guardado <strong>no se modifica</strong> y va a
                seguir diciendo el texto original. Si reenviás esta factura por correo, el cliente
                recibe el PDF corregido y ese XML en el mismo mensaje.
              </p>
              <p>No se tocan montos, impuestos, fechas ni el receptor. Queda registrado quién lo hizo y por qué.</p>
            </div>
          </div>

          {/* Línea a corregir */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Línea</label>
            {lineas.length === 1 ? (
              <div className="text-sm text-gray-700 border border-gray-200 rounded-lg px-3 py-2 bg-gray-50">
                Línea {linea?.numeroLinea ?? linea?.numero_linea ?? 1}
                {linea?.codigoComercial || linea?.codigo_comercial ? ` · ${linea.codigoComercial || linea.codigo_comercial}` : ''}
              </div>
            ) : (
              <select value={lineaId ?? ''} onChange={e => setLineaId(Number(e.target.value))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-200">
                {lineas.map((l, i) => (
                  <option key={l.id} value={l.id}>
                    {`Línea ${l.numeroLinea ?? l.numero_linea ?? i + 1} · ${(l.detalle || '').slice(0, 60)}`}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Texto actual */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Dice ahora (y es lo que está en el XML de Hacienda)</label>
            <div className="text-sm text-gray-600 border border-gray-200 rounded-lg px-3 py-2 bg-gray-50 font-mono whitespace-pre-wrap break-words">
              {original || <span className="text-gray-400">(vacío)</span>}
            </div>
          </div>

          {/* Texto nuevo */}
          <div>
            <div className="flex items-baseline justify-between mb-1">
              <label className="block text-xs font-medium text-gray-500">Debe decir</label>
              <span className={`text-[11px] tabular-nums ${largo > MAX_DETALLE ? 'text-red-600 font-semibold' : 'text-gray-400'}`}>
                {largo}/{MAX_DETALLE}
              </span>
            </div>
            <textarea value={descripcion} onChange={e => setDescripcion(e.target.value)} rows={3}
              className={`w-full border rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 ${
                largo > MAX_DETALLE ? 'border-red-400 focus:ring-red-200' : 'border-gray-200 focus:ring-amber-200'
              }`} />
            {largo > MAX_DETALLE && (
              <p className="mt-1 text-xs text-red-600">Se pasa por {largo - MAX_DETALLE} caracter(es) del máximo del campo.</p>
            )}
          </div>

          {/* Motivo */}
          <div>
            <div className="flex items-baseline justify-between mb-1">
              <label className="block text-xs font-medium text-gray-500">Motivo de la corrección <span className="text-red-500">*</span></label>
              <span className={`text-[11px] ${motivoOk ? 'text-gray-400' : 'text-amber-600'}`}>
                {motivoOk ? 'ok' : `mínimo ${MIN_MOTIVO} caracteres`}
              </span>
            </div>
            <textarea value={motivo} onChange={e => setMotivo(e.target.value)} rows={2}
              placeholder="Ej: se dejó el mes anterior por error, la FE corresponde a septiembre"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-amber-200" />
          </div>

          {/* Bitácora */}
          {historial.length > 0 && (
            <div>
              <button onClick={() => setVerHistorial(v => !v)}
                className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700">
                <History className="h-3.5 w-3.5" />
                {verHistorial ? 'Ocultar' : `Ver correcciones anteriores (${historial.length})`}
              </button>
              {verHistorial && (
                <div className="mt-2 border border-gray-200 rounded-lg divide-y divide-gray-100 text-xs">
                  {historial.map(h => (
                    <div key={h.id} className="px-3 py-2">
                      <div className="flex justify-between text-gray-500">
                        <span>{h.user_email || 'desconocido'}</span>
                        <span>{new Date(h.created_at).toLocaleString('es-CR')}</span>
                      </div>
                      <div className="mt-1 text-gray-700">
                        <span className="line-through text-gray-400">{h.valor_anterior}</span>
                        {' → '}
                        <span className="font-medium">{h.valor_nuevo}</span>
                      </div>
                      <div className="mt-0.5 text-gray-500 italic">{h.motivo}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50 rounded-b-xl shrink-0">
          <button onClick={() => !guardando && onClose()}
            className="px-5 py-2 border-2 border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium">
            Cancelar
          </button>
          <button onClick={guardar} disabled={!puedeGuardar}
            className="px-6 py-2 border-2 border-amber-600 bg-amber-600 text-white rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-50 hover:bg-amber-700">
            <PenLine className="h-4 w-4" />
            {guardando ? 'Guardando...' : 'Corregir descripción'}
          </button>
        </div>
      </div>
    </div>
  );
}
